// src/components/Hermes/MessageList.tsx

import { useEffect, useRef } from 'react';
import { HermesIntent } from '../../types/hermes.types';
import type { HermesMessage, MessageStatus } from '../../types/hermes.types';
import { RuntimeName } from '../../types/runtime';

interface MessageListProps {
  messages: HermesMessage[];
  loading?: boolean;
}

export default function MessageList({ messages, loading }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const getStatusIcon = (status: MessageStatus) => {
    switch (status) {
      case 'loading':
        return '⏳';
      case 'streaming':
        return '▶️';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '';
    }
  };

  const getStatusColor = (status: MessageStatus) => {
    switch (status) {
      case 'loading':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'streaming':
        return 'bg-blue-500/20 text-blue-400';
      case 'success':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'error':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-white/5 text-gray-400';
    }
  };

  const getRuntimeIcon = (runtime?: RuntimeName) => {
    switch (runtime) {
      case RuntimeName.Hermes:
        return '🧠';
      case RuntimeName.Quant:
        return '📈';
      case RuntimeName.Knowledge:
        return '📚';
      case RuntimeName.Business:
        return '🏢';
      default:
        return '🤖';
    }
  };

  // 意图路由徽章颜色映射（不同意图使用不同配色，便于可视化区分）
  const getIntentColor = (intent?: HermesIntent): string => {
    if (!intent) return 'bg-white/10 text-gray-400';
    switch (intent) {
      case HermesIntent.Chat:
        return 'bg-blue-500/20 text-blue-300';
      case HermesIntent.Knowledge:
        return 'bg-emerald-500/20 text-emerald-300';
      case HermesIntent.Plan:
        return 'bg-purple-500/20 text-purple-300';
      case HermesIntent.Trading:
        return 'bg-amber-500/20 text-amber-300';
      case HermesIntent.Business:
        return 'bg-cyan-500/20 text-cyan-300';
      case HermesIntent.Agent:
        return 'bg-indigo-500/20 text-indigo-300';
      case HermesIntent.Content:
        return 'bg-pink-500/20 text-pink-300';
      case HermesIntent.Summarize:
        return 'bg-teal-500/20 text-teal-300';
      case HermesIntent.Translate:
        return 'bg-orange-500/20 text-orange-300';
      default:
        return 'bg-white/10 text-gray-300';
    }
  };

  // 意图标签格式化：首字母大写（chat → Chat）
  const formatIntentLabel = (intent?: HermesIntent): string => {
    if (!intent) return '';
    return intent.charAt(0).toUpperCase() + intent.slice(1);
  };

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
        <div className="text-center">
          <div className="text-2xl mb-2">💬</div>
          <p>输入消息开始与 Hermes 对话</p>
          <p className="text-xs text-gray-600 mt-1">支持意图识别 · 自动切换 Workspace</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-white/10"
    >
      {messages.map((message) => {
        // 从 payload 中提取意图路由信息（ragUsed / model 等），用于显示路由可视化徽章
        const payload = message.payload as
          | { model?: string; ragUsed?: boolean; ragCardsCount?: number }
          | undefined;
        return (
        <div
          key={message.id}
          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`
              max-w-[85%] rounded-xl px-4 py-2.5 text-sm
              ${message.role === 'user'
                ? 'bg-blue-500/30 text-white'
                : message.status === 'error'
                  ? 'bg-red-500/20 text-red-300'
                  : 'bg-white/10 text-gray-200'
              }
              ${message.status === 'loading' || message.status === 'streaming' ? 'animate-pulse' : ''}
            `}
          >
            {/* 消息内容 */}
            {message.content ? (
              <div className="whitespace-pre-wrap">{message.content}</div>
            ) : message.status === 'loading' ? (
              <div className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="inline-block w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="inline-block w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            ) : null}

            {/* 元数据标签 */}
            {message.status !== 'loading' && message.status !== 'idle' && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {/* 状态 */}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${getStatusColor(message.status)}`}>
                  {getStatusIcon(message.status)} {message.status}
                </span>

                {/* Runtime */}
                {message.runtime && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400">
                    {getRuntimeIcon(message.runtime as RuntimeName)} {message.runtime}
                  </span>
                )}

                {/* 意图徽章（带颜色，可视化路由） */}
                {message.intent && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${getIntentColor(message.intent)}`}>
                    🎯 {formatIntentLabel(message.intent)}
                  </span>
                )}

                {/* RAG 徽章（仅 AI 回复，绿色=命中 / 灰色=未命中） */}
                {message.role === 'assistant' && payload && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${payload.ragUsed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-gray-400'}`}>
                    {payload.ragUsed ? '📚 RAG' : '无 RAG'}
                  </span>
                )}

                {/* 模型徽章（仅 AI 回复且有 model 信息） */}
                {message.role === 'assistant' && payload?.model && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300">
                    🤖 {payload.model}
                  </span>
                )}

                {/* Widgets */}
                {message.widgets?.map((widget) => (
                  <span key={widget} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                    📦 {widget}
                  </span>
                ))}

                {/* View */}
                {message.view && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                    🔄 {message.view}
                  </span>
                )}
              </div>
            )}

            {/* 错误信息 */}
            {message.error && (
              <div className="mt-1 text-xs text-red-400/70">
                ⚠️ {message.error}
              </div>
            )}

            {/* 时间戳 */}
            <div className="mt-1 text-[10px] text-gray-500 text-right">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}